import React, { useState, useEffect, useRef } from "react";
import { HashLink } from 'react-router-hash-link';

import axios from "axios";
import useAxios from "../utils/useAxios";
import '../styles/table.css';

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCode, faCodeBranch, faSyncAlt, faDownload, faUpload, faRocket, faCheckCircle, faSpinner } from "@fortawesome/free-solid-svg-icons";
import ReactHero from "../assets/img/technologies/Logo_ME_color_white_RGB.png";
import { Col, Row, Card, Image, Container, Form, Navbar, Button, Accordion, Table, Modal, Alert } from '@themesberg/react-bootstrap';

// How often to poll the backend for commit/build status (ms). Kept short so the
// spinner reflects status changes quickly; retry counts preserve the overall
// timeout window.
const COMMIT_POLL_INTERVAL_MS = 5000; // 5s x 24 retries = 2 min ceiling
const COMMIT_POLL_MAX_RETRIES = 24;
const BUILD_POLL_INTERVAL_MS = 15000; // 15s x 120 retries = 30 min ceiling
const BUILD_POLL_MAX_RETRIES = 120;

// Component for displaying configuration metadata
const ConfigMetadataDisplay = ({ summary, fileId }) => {
  const details = summary[fileId];
  if (!details) return null; // Don't render if no details are found

  return (
      <div className="metadata-box">
          <p><strong>Uploader:</strong> {details.uploader}</p>
          <p><strong>Version:</strong> {details.customer_version}</p>
          <p><strong>Description:</strong> {details.description}</p>
          <p><strong>Date:</strong> {new Date(details.upload_date).toLocaleString()}</p>
      </div>
  );
};

// Component for displaying library metadata
const LibraryMetadataDisplay = ({ summary, fileId }) => {
  const details = summary[fileId];
  // Handle cases where older libraries might not have metadata
  if (!details) {
      return <p className="text-muted mt-2">Metadata not available for this library.</p>;
  }

  return (
      <div className="metadata-box">
          <p><strong>Uploader:</strong> {details.uploader}</p>
          <p><strong>Description:</strong> {details.description}</p>
          <p><strong>Original Filename:</strong> {details.filename}</p>
          <p><strong>Upload Date:</strong> {new Date(details.upload_date).toLocaleString()}</p>
      </div>
  );
};

const LinkLogViewer = ({ lines, isLoading, onRefresh }) => {
  return (
    <Card bg="light" className="mt-3">
      <Card.Title className="ms-3 mt-3 d-flex justify-content-between align-items-center">
        <span>Linker Log</span>
        <Button variant="light" onClick={onRefresh} disabled={isLoading}>
          <FontAwesomeIcon icon={faSyncAlt} className={isLoading ? "fa-spin" : ""} />
        </Button>
      </Card.Title>
      <Card.Body>
        {isLoading ? (
          <div className="text-center py-4">
            <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
            Loading linker log...
          </div>
        ) : lines.length === 0 ? (
          <p className="text-muted mb-0">No linker log available for this build.</p>
        ) : (
          <div className="link-log-viewer">
            {lines.map((line, index) => (
              <div key={index} className="link-log-line">
                <span className="link-log-line-number">{index + 1}</span>
                <span className="link-log-line-text">{line || " "}</span>
              </div>
            ))}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

const SCTConfig = () => {
  const api = useAxios();

  const [releaseIds, setReleaseIds] = useState([]);
  const [releaseId, setReleaseId] = useState(null);
  const [releasesError, setReleasesError] = useState(false);

  const [patchIds, setPatchIds] = useState([]);
  const [patchId, setPatchId] = useState(null);

  const [buildIds, setBuildIds] = useState([]);
  const [buildId,  setBuildId] = useState(null);

  // --- Loading states to provide user feedback ---
  const [isLoadingPatches, setIsLoadingPatches] = useState(false);
  const [isLoadingPatchDetails, setIsLoadingPatchDetails] = useState(false);
  const [isLoadingProjectFiles, setIsLoadingProjectFiles] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isCreatingPatch, setIsCreatingPatch] = useState(false);

  // --- STATE FOR FILE SELECTION FLOW ---
  const [availableVariants, setAvailableVariants] = useState([]);
  const [selectedVariant, setSelectedVariant] = useState("");
  const [configSummary, setConfigSummary] = useState({});
  const [selectedConfigFile, setSelectedConfigFile] = useState("");
  const [librarySummary, setLibrarySummary] = useState({});
  const [selectedLibFile, setSelectedLibFile] = useState("");
  const [newlyUploadedLib, setNewlyUploadedLib] = useState(null); // To track the file for upload
  const [uploadName, setUploadName] = useState("");
  const [uploadDescription, setUploadDescription] = useState("");

  // Project selection state
  const [selectedProject, setSelectedProject] = useState("");
  const [projectSelectorEnabled, setProjectSelectorEnabled] = useState(false);
  const [availableProjects, setAvailableProjects] = useState([]); // Remove hardcoded array

  const [buildLog, setBuildLog] = useState(null);
  const [buildLoading, setBuildLoading] = useState(false); // Track build loading state
  const [buildStage, setBuildStage] = useState('Queuing'); // Default to "Queuing"
  const buildSuccessRef = useRef(null);
  const buildFailedRef = useRef(null);
  const [hasLinkLog, setHasLinkLog] = useState(false);
  const [linkLogLines, setLinkLogLines] = useState([]);
  const [isLoadingLinkLog, setIsLoadingLinkLog] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [description, setDescription] = useState("");

  const pollingBuildInProgress = useRef(false); // Tracks if polling is currently happening

  // Load build loading state from localStorage when the component mounts
  useEffect(() => {
    const savedBuildLoading = sessionStorage.getItem("buildLoading");
    if (savedBuildLoading === "true") {
      setBuildLoading(true); // Restore the loading state
    }
  }, []);

  // Persist the buildLoading state to localStorage whenever it changes
  useEffect(() => {
    sessionStorage.setItem("buildLoading", buildLoading);
  }, [buildLoading]);

  const [patchCommitted, setPatchCommitted] = useState(false); // state for UI re-rendering (includes 'accepted' and 'success')
  const [patchCommitSuccess, setPatchCommitSuccess] = useState(false); // true only when commit_status is 'success'
  const patchFailedRef = useRef(null);

  const [commitLoading, setCommitLoading] = useState(false); // New loading state
  // Persist loading state in localStorage
  useEffect(() => {
    const savedLoading = sessionStorage.getItem("commitLoading");
    if (savedLoading === "true") {
      setCommitLoading(true); // Set loading state from localStorage
    }
  }, []);

  const handleReleaseId = async (id) => {
    setReleaseId(id);
    sessionStorage.setItem("releaseId", id);
    handlePatchId(null);
    handleBuildId(null);
    // Reset project selector state when release changes
    setProjectSelectorEnabled(false);
    setSelectedProject("");
    setAvailableProjects([]);
  };

  const handlePatchId = (id) => {
    setPatchId(id);
    sessionStorage.setItem("patchId", id);
    handleBuildId(null);

    if (id === null || id === "") {
        // Reset all relevant state when no patch is selected
        setSelectedProject("");
        setProjectSelectorEnabled(false);
        setAvailableProjects([]);
        setSelectedLibFile("");
        setSelectedConfigFile("");
        setSelectedVariant("");
        setNewlyUploadedLib(null);
        setPatchCommitted(false);
        setPatchCommitSuccess(false);
        setLinkLogLines([]);
        setHasLinkLog(false);
        patchFailedRef.current = false;
    }
  };

  const checkProjectSelectorEnabledForRelease = async (releaseId, patchId) => {
    if (!releaseId || !patchId) return;

    // --- CACHING LOGIC using sessionStorage ---
    const cacheKey = `project-config-${releaseId}`;
    const cachedData = sessionStorage.getItem(cacheKey);

    // 1. If data exists in sessionStorage, use it.
    if (cachedData) {
      const projects = JSON.parse(cachedData).projects || [];
      setAvailableProjects(projects);
      setProjectSelectorEnabled(projects.length > 0);
      return; // Stop the function here
    }

    // 2. If not cached, fetch from the API.
    try {
      const response = await api.get(`/releases/${releaseId}/project-config`);
      const projectData = response.data;

      // 3. Store the API response in sessionStorage for next time.
      sessionStorage.setItem(cacheKey, JSON.stringify(projectData));

      // 4. Update the component state.
      const projects = projectData.projects || [];
      setAvailableProjects(projects);
      setProjectSelectorEnabled(projects.length > 0);

    } catch (error) {
      console.error("Failed to get project config for patch:", error);
      setProjectSelectorEnabled(false);
      setAvailableProjects([]);
      setSelectedProject("");
    }
  };

  const handleBuildId = (id) => {
    setBuildId(id);
    sessionStorage.setItem("buildId", id);
    if (id === null) {
      setBuildLoading(false); // Stop loading if buildId is cleared
      buildSuccessRef.current = false;
      setBuildLog(null); // Clear the logs if no build ID is selected
      setHasLinkLog(false);
      setLinkLogLines([]);
    } else {
      getBuildLog(releaseId, patchId, id); // Fetch the logs for the selected build
    }
  };

  const getReleases = async () => {
    // --- CACHING LOGIC using sessionStorage ---
    const cacheKey = "all-releases";
    const cachedData = sessionStorage.getItem(cacheKey);

    // 1. If data exists in sessionStorage, use it.
    if (cachedData) {
        setReleaseIds(JSON.parse(cachedData).release_ids);
        return; // Stop the function here
    }

    // 2. If not cached, fetch from the API.
    try {
      const response = await api.get("/releases");
      const releasesData = response.data;

      // 3. Cache only non-empty results: a cached empty list would suppress
      // every refetch for the rest of the tab session.
      if (releasesData.release_ids?.length > 0) {
        sessionStorage.setItem(cacheKey, JSON.stringify(releasesData));
      }

      // 4. Update the component state.
      setReleaseIds(releasesData.release_ids || []);
      setReleasesError(false);

    } catch (error) {
      console.error("Failed to fetch release IDs", error);
      setReleasesError(true);
    }
  };


  const getPatches = async () => {
    setIsLoadingPatches(true);
    try {
      const response = await api.get(`/releases/${releaseId}/patches`);
      const patches = response.data;
      sessionStorage.setItem("patches", JSON.stringify(patches));
      setPatchIds(patches.patch_ids);
    } catch {
      console.error("Failed to fetch patch IDs.");
      setPatchIds([]);
    } finally {
      setIsLoadingPatches(false);
    }
  };

  const getPatch = async (releaseId, patchId) => {
    if (!releaseId || !patchId) return null;
    try {
        const response = await api.get(`/releases/${releaseId}/patches/${patchId}`);
        const patch = response.data;
        if (patch) {
            // Set project and commit status
            if (patch.project) setSelectedProject(patch.project);

            // Treat both 'success' and 'accepted' as committed states (for locking UI)
            const isCommitted = patch.commit_status === 'success' || patch.commit_status === 'accepted';
            setPatchCommitted(isCommitted);

            // Only 'success' status allows triggering builds
            const isCommitSuccess = patch.commit_status === 'success';
            setPatchCommitSuccess(isCommitSuccess);

            // Correctly set file and variant names from the patch data
            if (patch.upload_file_name) {
                setSelectedLibFile(patch.upload_file_name);
            }
            if (patch.config_file_name) {
                setSelectedConfigFile(patch.config_file_name);
            }
            if (patch.config_variants) {
                setSelectedVariant(patch.config_variants);
            }

            // Return the fresh status so pollers don't rely on stale state.
            return { isCommitted, isCommitSuccess };
        }
        return null;
    } catch (error) { console.error("Failed to fetch patch:", error); return null; }
  };

  const getConfigSummary = async (releaseId, project) => {
    try {
        const response = await api.get(`/releases/${releaseId}/config/${project}`);
        const fullSummary = response.data || {};

        // Destructure the 'defaults' key out and keep the rest of the object
        const { defaults, ...filteredSummary } = fullSummary;

        // Set the state with the summary that no longer contains the 'defaults' key
        setConfigSummary(filteredSummary);
    } catch (error) {
        console.error("Failed to fetch config summary:", error);
        setConfigSummary({});
    }
  };

  const getVariantsForConfig = async (releaseId, project, configFile) => {
    // Fetches variants for a chosen config file
    if (!configFile) {
        setAvailableVariants([]);
        return;
    }
    try {
        const response = await api.get(`/releases/${releaseId}/config/${project}/${configFile}/variants`);
        const variants = response.data || [];

        setAvailableVariants(variants);

        if (variants.length === 1) {
            setSelectedVariant(variants[0]);
        }

    } catch (error) {
        console.error("Failed to fetch variants:", error);
        setAvailableVariants([]);
    }
  };

  const getLibrarySummary = async (releaseId, project) => {
    try {
        const response = await api.get(`/releases/${releaseId}/patches/library-files`, {
            params: { project_id: project }
        });
        setLibrarySummary(response.data || {});
    } catch (error) {
        console.error("Failed to fetch library summary:", error);
        setLibrarySummary({});
    }
  };

  const uploadLibraryFile = async () => {
    if (!newlyUploadedLib || !selectedProject) {
        console.error("Library file or project not selected for upload.");
        return;
    }
    setIsUploading(true);
    try {
        const formData = new FormData();
        formData.append('file', newlyUploadedLib);
        formData.append('projectId', selectedProject);
        formData.append('name', uploadName);
        formData.append('description', uploadDescription);

        const response = await api.post(
            `/releases/${releaseId}/patches/upload`,
            formData
        );

        if (response && response.data.presigned_url) {
            await axios.put(response.data.presigned_url, newlyUploadedLib, {
                headers: { 'Content-Type': newlyUploadedLib.type },
            });

            // Refresh summary, set selection, and reset upload fields
            await getLibrarySummary(releaseId, selectedProject);
            setSelectedLibFile(response.data.filename);
            setNewlyUploadedLib(null);
            setUploadName(""); // <-- RESET optional name
            setUploadDescription("");
            document.getElementById('libraryFileInput').value = "";
        }
    } catch (error) {
        console.error(`Failed to upload file:`, error);
        alert("File upload failed. Check the console for details.");
    } finally {
      setIsUploading(false); // <-- Stop uploading
    }
  };

  const getBuilds = async () => {
    if (releaseId !== null && patchId !== null) {
      try {
        const response = await api.get(`/releases/${releaseId}/patches/${patchId}/builds`);
        const builds = response.data;
        sessionStorage.setItem("builds", JSON.stringify(builds));
        setBuildIds(builds.build_ids);
      } catch {
        console.error("Failed to fetch build IDs.");
      }
    }
  };

  const getBuild = async (releaseId, patchId, buildId) => {
    if (releaseId && patchId && buildId) {
      try {
        const response = await api.get(`/releases/${releaseId}/patches/${patchId}/builds/${buildId}`);
        if (response && response.status === 200) {
          const build = response.data;

          // Set the current build stage
          // Update refs based on build success or failure
          if (build.build_status === "success") {
            buildSuccessRef.current = true;
            buildFailedRef.current = false;
            setHasLinkLog(false);
            setLinkLogLines([]);
            getBuildLog(releaseId,patchId,buildId);
            setBuildLoading(false);
          } else if (build.build_status === "failed") {
            buildSuccessRef.current = false;
            buildFailedRef.current = true;
            setHasLinkLog(build.has_link_log === true);
            if (build.has_link_log === true) {
              getLinkLog(releaseId, patchId, buildId);
            } else {
              setLinkLogLines([]);
            }
            getBuildLog(releaseId,patchId,buildId);
            setBuildLoading(false);
          } else if (build.build_status === "accepted") {
            if (build.build_stage) {
              setBuildStage(build.build_stage); // Set the build stage from API response
            } else {
              setBuildStage("Queuing");
            }
            buildSuccessRef.current = false;
            buildFailedRef.current = false;
            setHasLinkLog(false);
            setLinkLogLines([]);
            setBuildLoading(true); // Set loading to true when build starts
            if (!pollingBuildInProgress.current) {
              pollBuildStatus(buildId);
            }
          }
        }
      } catch (error) {
        console.error("Failed to fetch build.");
        buildFailedRef.current = true; // Mark as failed if fetch failed
      }
    }
  };

  const getBuildLog = async (releaseId, patchId, buildId) => {
    if (releaseId && patchId && buildId) {
      try {
        const response = await api.get(`/releases/${releaseId}/patches/${patchId}/builds/${buildId}/log`);
        const buildLog = response.data;
        if (buildLog) {
          setBuildLog(buildLog.lines);
        }
      } catch {
        console.error("Failed to fetch patch.");
      }
    }
  };

  const getLinkLog = async (releaseId, patchId, buildId) => {
    if (releaseId && patchId && buildId) {
      setIsLoadingLinkLog(true);
      try {
        const response = await api.get(`/releases/${releaseId}/patches/${patchId}/builds/${buildId}/link-log/content`);
        const linkLog = response.data;
        setLinkLogLines(linkLog?.lines || []);
      } catch {
        console.error("Linker log not available for this build.");
        setLinkLogLines([]);
      } finally {
        setIsLoadingLinkLog(false);
      }
    }
  };

  const downloadBuild = async () => {
    if (releaseId && patchId && buildId) {
      try {
        const response = await api.get(`/releases/${releaseId}/patches/${patchId}/builds/${buildId}/download`);
        if (response && response.status === 200) {
          const downloadUrl = response.data.download_url;
          const link = document.createElement('a');
          link.href = downloadUrl;
          link.download = `${releaseId}_${patchId}_${buildId}.tar.gz`;
          document.body.appendChild(link);
          link.click();
        }
      } catch {
        console.error("Failed to download build artifact.");
      }
    }
  };

  const createPatch = async () => {
    setIsCreatingPatch(true);
    try {
        const response = await api.post(`releases/${releaseId}/patches`);
        handlePatchId(null);
        if (response && response.status === 200) {
            const patch_id = response.data.patch_id;
            await getPatches(releaseId);
            setPatchId(patch_id);
            sessionStorage.setItem("patchId", patch_id);
        }
    } catch (error) {
        console.error("Failed to create patch:", error);
    } finally {
        setIsCreatingPatch(false);
    }
  };

  const handleLibFileSelect = (e) => {
    const selectedFile = e.target.value;
    setSelectedLibFile(selectedFile);
    // If a file is selected from the dropdown, clear the file input
    if (selectedFile) {
        setNewlyUploadedLib(null);
        // This clears the visible filename in the file input element
        document.getElementById('libraryFileInput').value = "";
    }
};

  const handleNewLibFileChange = (e) => {
      const file = e.target.files[0];
      setNewlyUploadedLib(file);
      // If a new file is chosen, reset the dropdown selection
      if (file) {
          setSelectedLibFile("");
      }
  };

  const commitPatch = async () => {
    // The condition for committing is that a library file has been selected.
    if (releaseId && patchId && selectedLibFile) {
        if (patchCommitted) {
            return;
        }
        setCommitLoading(true);
        try {
          await api.post(`releases/${releaseId}/patches/${patchId}/select-files`, {
              library_file: selectedLibFile,
              config_file: selectedConfigFile || "",
              config_variants: selectedVariant || "", // New field for variant
              project: selectedProject || ""
          });

          const response = await api.post(`releases/${releaseId}/patches/${patchId}/commit`);
          if (response && response.status === 200) {
            let retries = 0;
            let committed = false;
            // Poll the patch's fresh status (not the stale `patchCommitted`
            // state, which does not update inside this running loop).
            while (!committed && retries <= COMMIT_POLL_MAX_RETRIES) {
              retries++;
              const result = await getPatch(releaseId, patchId);
              committed = result?.isCommitted ?? false;

              if (committed) {
                break;
              }

              if (retries < COMMIT_POLL_MAX_RETRIES) {
                await new Promise((r) => setTimeout(r, COMMIT_POLL_INTERVAL_MS));
              }
            }

            if (!committed) {
              console.error("Max retries reached, commit status still pending.");
            }
          }
        } catch (error) {
          console.error("Failed to commit patch.", error);
        } finally {
          setCommitLoading(false); // Stop loading after patch is committed or polling stops
        }
    } else {
      console.error("No release or patch selected.");
    }
  };

  const createBuild = async ({ description }) => {
    if (releaseId && patchId && patchCommitSuccess) {
      setBuildLog(null);
      setLinkLogLines([]);
      setHasLinkLog(false);
      buildSuccessRef.current = null;
      buildFailedRef.current = null;
      setBuildLoading(true);

      try {
        const payload = { description };
        // Project is now taken from the patch, not from user input

        const response = await api.post(
          `releases/${releaseId}/patches/${patchId}/builds`,
          payload
        );

        if (response && response.status === 200) {
          const newBuildId = response.data.build_id;
          setBuildId(newBuildId);
          sessionStorage.setItem("buildId", newBuildId);
          await getBuilds();
          setBuildId(newBuildId);
          pollBuildStatus(newBuildId);
        }
      } catch (error) {
        console.error("Failed to trigger new build. Error:", error);
      } finally {
        setBuildLoading(false);
        sessionStorage.removeItem("buildLoading");
        pollingBuildInProgress.current = false;
      }
    } else {
      console.error("No release or patch selected or patch not committed.");
    }
  };

  const pollBuildStatus = async (build_id) => {
    if (pollingBuildInProgress.current) {
      return;
    }
    pollingBuildInProgress.current = true; // Mark polling as started
    try {
      let retries = 0;

      while (!buildSuccessRef.current && !buildFailedRef.current && retries <= BUILD_POLL_MAX_RETRIES) {
        retries++;
        await getBuild(releaseId, patchId, build_id); // Fetch the build status

        if (buildSuccessRef.current) {
          pollingBuildInProgress.current = false; // Mark polling as finished
          break;
        }
        if (buildFailedRef.current) {
          pollingBuildInProgress.current = false; // Mark polling as finished
          break;
        }

        if (retries < BUILD_POLL_MAX_RETRIES) {
          await new Promise((r) => setTimeout(r, BUILD_POLL_INTERVAL_MS));
        }
      }

      if (retries > BUILD_POLL_MAX_RETRIES) {
        pollingBuildInProgress.current = false; // Mark polling as finished
        console.error("Max retries reached. Build status polling stopped.");
      }
    } catch (error) {
      console.error("Error fetching build status:", error);
    } finally {
      pollingBuildInProgress.current = false; // Ensure polling is reset if there's an error
    }
  };

  // Read state from local storage on page reload
  useEffect(() => { getReleases(); }, []);
  useEffect(() => {
    if (releaseId) {
      getPatches();
    } else {
      setPatchIds([]); // Clear patches if no release is selected
    }
  }, [releaseId]);


  useEffect(() => {
    const loadPatchData = async () => {
        if (releaseId && patchId) {
            // Step 1: Immediately check for projects. This is usually fast due to caching.
            // This makes the "Project Selection" card appear right away.
            await checkProjectSelectorEnabledForRelease(releaseId, patchId);

            // Step 2: Now show loading while fetching the rest of the patch data.
            setIsLoadingPatchDetails(true);
            try {
                await Promise.all([
                    getPatch(releaseId, patchId),
                    getBuilds()
                ]);
            } catch (error) {
                console.error("Failed to load patch details", error);
            } finally {
                setIsLoadingPatchDetails(false);
            }
        } else {
            setSelectedProject("");
            setProjectSelectorEnabled(false);
        }
    };
    loadPatchData();
  }, [patchId, releaseId]);

  useEffect(() => {
    const loadProjectFiles = async () => {
        if (releaseId && patchId && selectedProject) {
            setIsLoadingProjectFiles(true); // <-- Start loading project files
            try {
                await Promise.all([
                    getLibrarySummary(releaseId, selectedProject),
                    getConfigSummary(releaseId, selectedProject)
                ]);
            } catch (error) {
                console.error("Failed to load project files", error);
            } finally {
                setIsLoadingProjectFiles(false); // <-- Stop loading project files
            }
        } else {
            setLibrarySummary({});
            setConfigSummary({});
        }

        if (!patchCommitted) {
            setSelectedLibFile("");
            setSelectedConfigFile("");
        }
    };
    loadProjectFiles();
  }, [selectedProject, patchId, releaseId, patchCommitted]);

  useEffect(() => {
    // When the user chooses a config file (like 'defaults' or 'config-1'), fetch its variants.
    if (releaseId && selectedProject && selectedConfigFile) {
        getVariantsForConfig(releaseId, selectedProject, selectedConfigFile);
    } else {
        // Clear variants if no config file is selected
        setAvailableVariants([]);
    }
    // Reset the final variant selection
    setSelectedVariant("");
  }, [selectedConfigFile, releaseId]); // Note: selectedProject is implicitly handled

  // Fetch build details and logs when buildId changes
  useEffect(() => {
    if (releaseId && patchId && buildId) {
      getBuild(releaseId, patchId, buildId); // Fetches build details
      getBuildLog(releaseId, patchId, buildId); // Fetches build logs
    }
  }, [buildId]);

  return (
    <>
      <section id="SCTConfig" className="section-header overflow-hidden pt-6 pt-lg-6 pb-6 pb-lg-6 bg-primary text-white">
        <Container>
          <Row className="mb-2">
            <Col md={6} xs={9} className="text-left">
              <div className="react-big-icon d-none d-lg-block"><span className="fab fa-react"></span></div>
              <h1 className="fw-bolder text-secondary t-2"> Configuration </h1>
              <p className="text-muted fw-light mb-5 h5"> A Portal to Munich Electrification build system</p>
            </Col>
          </Row>
        </Container>
      </section>
      <section display="none" className="section section-lg pt-1">
        <Container>
          <Row className="mb-2">
            <Col md={3} className="text-left">
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label> Select Base Release</Form.Label>
                  <Form.Select
                    value={releaseId || ""}
                    onChange={e => handleReleaseId(e.target.value)} // Log the selected value
                  >
                    <option value="">Select Release Version</option>
                    {releaseIds.map(id => (
                      <option key={id} value={id}>
                        {id}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Form>
            </Col>
            <Col>
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label> Select or create a patch </Form.Label>
                  <Form.Select
                    value={patchId || ""} // Handle undefined or null patchId
                    disabled={!releaseId || isLoadingPatches}
                    onChange={e => handlePatchId(e.target.value)}
                  >
                    <option value="">
                      {isLoadingPatches ? "Loading patches..." : "Select Patch version"}
                    </option>
                    {patchIds.map(id => (
                      <option key={id} value={id}>
                        {id}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Form>
            </Col>
            <Col>
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>Select or create a build</Form.Label>
                  <Form.Select
                    value={buildId || ""} // Set value to empty string if no buildId is selected
                    disabled={!patchId}
                    onChange={e => {
                      const selectedValue = e.target.value;
                      if (selectedValue !== "") {
                        handleBuildId(selectedValue);  // Only set the buildId if a valid build is selected
                      }
                    }}
                  >
                    <option value="">Select build version</option> {/* Set value to empty string */}
                    {buildIds.map(id => (
                      <option key={id} value={id}>
                        {id}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Form>
            </Col>
            <Col className="d-flex flex-column">
              <Row className="mt-auto mb-3">
                <Button
                  className="btn-primary-dark"
                  variant="primary"
                  disabled={!releaseId || isCreatingPatch}
                  onClick={createPatch}
                >
                  {isCreatingPatch ? (
                    <FontAwesomeIcon icon={faSpinner} spin className="me-1" />
                  ) : (
                    <FontAwesomeIcon icon={faCodeBranch} className="me-1" />
                  )}
                  {isCreatingPatch ? "Creating..." : "Create new Patch"}
                </Button>
              </Row>
            </Col>
          </Row>

            {releasesError && (
              <Row className="mb-2">
                <Col>
                  <Alert variant="danger">
                    Could not reach the SCT API to load the available releases.
                    If this keeps happening, your network or corporate proxy may
                    be blocking the connection — please contact ME support.
                  </Alert>
                </Col>
              </Row>
            )}
            {!releaseId ? (
              <Row className="mb-2">
                <p>Please select a release version</p>
              </Row>
            ) : null}
            {releaseId && !patchId && (
              <Row className="mb-2"><p>Please select or create a patch version</p></Row>
            )}

            {/* Project Selector - Show when there are any available projects */}
            {releaseId && patchId && projectSelectorEnabled && (
              <Row className="mb-2">
                <Col>
                  <Card bg="light">
                    <Card.Title className="ms-1 mt-1">Project Selection</Card.Title>
                    <Card.Body>
                      <Row className="align-items-center">
                        <Col md={2} xs={3} className="text-right">
                          <Form.Label>Project:</Form.Label>
                        </Col>
                        <Col md={4} xs={6}>
                          <Form.Select
                            value={selectedProject}
                            disabled={
                              isLoadingPatchDetails ||
                              patchCommitted ||
                              commitLoading
                            }
                            onChange={(e) => {
                                setSelectedProject(e.target.value);
                            }}
                          >
                            <option value="">Select Project</option>
                            {availableProjects.map(project => (
                              <option key={project} value={project}>
                                {project}
                              </option>
                            ))}
                          </Form.Select>
                        </Col>
                        {selectedProject && (
                          <Col md={6} xs={3} className="d-flex justify-content-start align-items-center">
                              <FontAwesomeIcon icon={faCheckCircle} color="green" className="me-2" />
                              <span>Project: {selectedProject}</span>
                          </Col>
                        )}
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}
            {/* --- Global Loading Indicator for Patch Details --- */}
            {isLoadingPatchDetails && (
                <Row className="mb-2 justify-content-center text-center">
                    <Col xs="auto">
                        <FontAwesomeIcon icon={faSpinner} spin size="2x" className="text-primary mb-2" />
                        <p className="text-muted">Loading patch details...</p>
                    </Col>
                </Row>
            )}
            {/* Help text when project selector is enabled but no project selected */}
            {!isLoadingPatchDetails && releaseId && patchId && projectSelectorEnabled && !selectedProject && availableProjects.length > 1 && (
              <Row className="mb-2">
                <p className="text-muted">Please select a project to continue with file upload.</p>
              </Row>
            )}

            {/* Help text for file upload requirements - only show when config is determined */}
            {releaseId && patchId && !selectedLibFile && (
              (projectSelectorEnabled === false || selectedProject) && (
                <Row className="mb-2">
                  <p className="text-muted">Please upload the lib file to proceed.</p>
                </Row>
              )
            )}
            {/* --- Loading Indicator for Project Files --- */}
            {isLoadingProjectFiles && !isLoadingPatchDetails && (
                <Row className="mb-2 justify-content-center text-center">
                    <Col xs="auto">
                        <FontAwesomeIcon icon={faSpinner} spin size="2x" className="text-primary mb-2" />
                        <p className="text-muted">Loading project files...</p>
                    </Col>
                </Row>
            )}
            {/* --- Conditional Configuration Selection Card --- */}
            {/* This entire card will only appear if variants are available for the project */}
            {/* --- Configuration and Library Cards will now only show after loading --- */}
            {!isLoadingPatchDetails && !isLoadingProjectFiles && (
              <>
                {releaseId && patchId && selectedProject && (Object.keys(configSummary).length > 0) && (
                    <Row className="mb-2">
                        <Col>
                            <Card bg="light" className={patchCommitted ? 'card-disabled' : ''}>
                                <Card.Title className="ms-3 mt-2">Configuration Selection</Card.Title>
                                    <Card.Body>
                                        {/* If patch is committed, show selected data as plain text */}
                                        {patchCommitted && selectedConfigFile ? (
                                            <>
                                                <Row className="align-items-center">
                                                    <Col md={3}><Form.Label>Selected Config:</Form.Label></Col>
                                                    <Col>
                                                        <strong>
                                                            {configSummary[selectedConfigFile]?.name || selectedConfigFile}
                                                            {selectedVariant && ` / ${selectedVariant}`}
                                                        </strong>
                                                    </Col>
                                                </Row>
                                                {/* Show metadata when committed */}
                                                <ConfigMetadataDisplay summary={configSummary} fileId={selectedConfigFile} />
                                            </>
                                        ) : patchCommitted && !selectedConfigFile ? (
                                            <p className="text-muted">No configuration file was used in this patch.</p>
                                        ) : (
                                          <>
                                              {/* --- CONFIGURATION DROPDOWN --- */}
                                              <Row className="mb-3 align-items-center">
                                                  <Col md={3}><Form.Label>Configuration Source:</Form.Label></Col>
                                                  <Col md={7}>
                                                      <Form.Select value={selectedConfigFile} onChange={e => setSelectedConfigFile(e.target.value)}>
                                                          <option value="">Select a configuration...</option>
                                                          {/* Map over the summary object to show name and use ID as value */}
                                                          {Object.entries(configSummary).map(([configId, details]) => (
                                                              <option key={configId} value={configId}>
                                                                  {details.name}
                                                              </option>
                                                          ))}
                                                      </Form.Select>
                                                  </Col>
                                              </Row>
                                              {/* Variant selector */}
                                              <Row className="align-items-center">
                                                  <Col md={3}><Form.Label>Select Variant:</Form.Label></Col>
                                                  <Col md={7}>
                                                      <Form.Select value={selectedVariant} onChange={e => setSelectedVariant(e.target.value)} disabled={!selectedConfigFile}>
                                                          <option value="">Select a variant...</option>
                                                          {availableVariants.map(variant => <option key={variant} value={variant}>{variant}</option>)}
                                                      </Form.Select>
                                                  </Col>
                                              </Row>
                                              {/* --- Metadata Display --- */}
                                              {selectedConfigFile && <ConfigMetadataDisplay summary={configSummary} fileId={selectedConfigFile} />}
                                            </>
                                        )}
                                    </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                )}

                {/* --- Library Selection & Upload Card --- */}
                {releaseId && patchId && (!projectSelectorEnabled || selectedProject) && (
                    <Row className="mb-2">
                        <Col>
                            <Card bg="light" className={patchCommitted ? 'card-disabled' : ''}>
                                <Card.Title className="ms-3 mt-2">Library Selection & Upload</Card.Title>
                                <Card.Body>
                                      {patchCommitted ? (
                                          <>
                                              <Row className="align-items-center">
                                                  <Col md={3}><Form.Label>Selected Library:</Form.Label></Col>
                                                  <Col><strong>{librarySummary[selectedLibFile]?.name || selectedLibFile}</strong></Col>
                                              </Row>
                                              {/* Show metadata when committed */}
                                              <LibraryMetadataDisplay summary={librarySummary} fileId={selectedLibFile} />
                                          </>
                                      ) : (
                                        <>
                                            {/* --- LIBRARY DROPDOWN --- */}
                                            <Row className="mb-3 align-items-center">
                                                <Col md={3}><Form.Label>Select Existing Library:</Form.Label></Col>
                                                <Col md={7}>
                                                    <Form.Select value={selectedLibFile} onChange={handleLibFileSelect} disabled={!!newlyUploadedLib}>
                                                        <option value="">Select a library...</option>
                                                        {/* Map over the summary object */}
                                                        {Object.entries(librarySummary).map(([libId, details]) => (
                                                            <option key={libId} value={libId}>{details.name}</option>
                                                        ))}
                                                    </Form.Select>
                                                </Col>
                                            </Row>

                                            <Row className="justify-content-center text-center my-2">
                                                <Col md={10}><hr style={{ borderTop: '1px solid #ccc' }} /></Col>
                                                <div style={{ marginTop: '-20px' }}><span className="bg-light px-2 text-muted">OR</span></div>
                                            </Row>

                                            <Row className="mt-3 align-items-center">
                                              <Col md={3}><Form.Label>Upload New Library:</Form.Label></Col>
                                              <Col md={7}>
                                                  <Form.Control type="file" accept=".a" id="libraryFileInput" onChange={handleNewLibFileChange} disabled={patchCommitted || selectedLibFile !== "" || isUploading} />
                                              </Col>
                                              <Col md={2}>
                                                  <Button variant="primary" disabled={patchCommitted || !newlyUploadedLib || isUploading} onClick={uploadLibraryFile}>
                                                      {isUploading ? (
                                                          <FontAwesomeIcon icon={faSpinner} spin className="me-1" />
                                                      ) : (
                                                          <FontAwesomeIcon icon={faUpload} className="me-1" />
                                                      )}
                                                      {isUploading ? "Uploading..." : "Upload"}
                                                  </Button>
                                              </Col>
                                          </Row>

                                            {/* --- Optional Name Input for Upload --- */}
                                            <Row className="mt-2">
                                                <Col md={3}><Form.Label>Library Name:</Form.Label></Col>
                                                <Col md={9}>
                                                    <Form.Control
                                                        type="text"
                                                        value={uploadName}
                                                        onChange={e => setUploadName(e.target.value)}
                                                        placeholder="Optional: A unique name will be assigned if left empty."
                                                        disabled={!newlyUploadedLib || patchCommitted}
                                                    />
                                                </Col>
                                            </Row>

                                            {/* --- Description input is now below the new Name input --- */}
                                            <Row className="mt-2">
                                                <Col md={3}><Form.Label>Description:</Form.Label></Col>
                                                <Col md={9}>
                                                    <Form.Control
                                                        as="textarea" rows={2}
                                                        value={uploadDescription}
                                                        onChange={e => setUploadDescription(e.target.value)}
                                                        placeholder="Optional: Add a description for this library..."
                                                        disabled={!newlyUploadedLib || patchCommitted}
                                                    />
                                                </Col>
                                            </Row>
                                            {/* --- METADATA DISPLAY --- */}
                                            {selectedLibFile && <LibraryMetadataDisplay summary={librarySummary} fileId={selectedLibFile} />}
                                        </>
                                    )}

                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                )}
              </>
            )}
            {/* Help sections */}
            {releaseId && patchId && selectedLibFile && !patchCommitted && !commitLoading && (
                <Row className="mb-2">
                  <p>Please commit the current patch to create a new build</p>
                </Row>
            )}

            {/* Show commit in progress message when status is 'accepted' */}
            {releaseId && patchId && patchCommitted && !patchCommitSuccess && (
                <Row className="mb-2 justify-content-center text-center">
                    <Col xs="auto">
                        <FontAwesomeIcon icon={faSpinner} spin size="2x" className="text-primary mb-2" />
                        <p className="text-muted">Commit in progress... Please wait for it to complete before triggering a build.</p>
                    </Col>
                </Row>
            )}

            {releaseId && patchId && selectedLibFile && patchFailedRef.current && (
              <Row className="mb-2">
                <p>Something went wrong while trying to submit the changes.</p>
              </Row>
            )}

            {/* Patch Commit and Build buttons */}
            {/* The condition should depend on a library file being selected */}
            {releaseId && patchId && selectedLibFile && (
                <Row className="mb-2">
                    <Col md={12} className="text-left">
                        <div className="d-flex flex-wrap gap-2">
                            <Button
                                // Also fix the disabled check here
                                disabled={!selectedLibFile || patchCommitted || commitLoading}
                                onClick={commitPatch}
                                variant="primary"
                            >
                                {commitLoading ? (
                                    <>
                                        <FontAwesomeIcon icon={faSpinner} spin className="me-1" />
                                        Committing...
                                    </>
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faCode} className="me-1" />
                                        Commit Changes
                                    </>
                                )}
                            </Button>

                    {/* Trigger Build */}
                    <Button
                    disabled={!patchCommitSuccess || buildLoading}
                    onClick={() => setShowModal(true)}
                      variant="primary"
                    >
                      {buildLoading ? (
                        <>
                          <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                          {buildStage || "Building"}...
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faRocket} className="me-2" />
                          Trigger new build
                        </>
                      )}
                    </Button>

                    {/* Download Artifacts */}
                    <Button
                      disabled={!buildId || !buildSuccessRef.current}
                      onClick={downloadBuild}
                      variant="primary"
                    >
                      <FontAwesomeIcon icon={faDownload} className="me-1" />
                      Download artifacts
                    </Button>

                  </div>

                  {/* Build Modal */}
                  <Modal show={showModal} onHide={() => setShowModal(false)}>
                    <Modal.Header closeButton>
                      <Modal.Title>Start a new build</Modal.Title>
                    </Modal.Header>

                    <Modal.Body>
                      {/* Description field */}
                      <Form.Group className="mb-3">
                        <Form.Label>Description (optional)</Form.Label>
                        <Form.Control
                          type="text"
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          placeholder="Add a description for this build"
                        />
                      </Form.Group>

                      {/* Show selected project info if available */}
                      {selectedProject && (
                        <div className="mb-3">
                          <span className="text-muted">Project: </span>
                          <strong>{selectedProject}</strong>
                        </div>
                      )}
                    </Modal.Body>

                    <Modal.Footer>
                      <Button variant="secondary" onClick={() => setShowModal(false)}>
                        Cancel
                      </Button>
                      <Button
                        variant="primary"
                        onClick={() => {
                          setShowModal(false);
                          setBuildLoading(true);
                          createBuild({ description }).finally(() => {   // Only pass description
                            setBuildLoading(false);
                            setBuildStage("");
                          });
                        }}
                      >
                        Start Build
                      </Button>
                    </Modal.Footer>
                  </Modal>

                </Col>
              </Row>
            )}

          {/* Build details */}
          {releaseId && patchId && buildId && (
            <Row className="mb-2">
              {hasLinkLog && (
                <Col md={12}>
                  <LinkLogViewer
                    lines={linkLogLines}
                    isLoading={isLoadingLinkLog}
                    onRefresh={() => getLinkLog(releaseId, patchId, buildId)}
                  />
                </Col>
              )}
              <Row className="mb-2">
                <Accordion defaultActiveKey="0">
                  <Accordion.Item eventKey="0">
                    <Accordion.Header>
                      <div className="d-flex justify-content-between align-items-center w-100">
                        Logs
                        <Button variant="light" onClick={(e) => {
                          e.stopPropagation(); // Prevent accordion from toggling
                          getBuildLog(releaseId, patchId, buildId); // Manually refresh logs
                        }}>
                          <FontAwesomeIcon icon={faSyncAlt} />
                        </Button>
                      </div>
                    </Accordion.Header>
                    <Accordion.Body>
                    <Table striped bordered hover responsive>
                    <thead>
                      <tr>
                        <th>Build Number</th>
                        <th>Timestamp</th>
                        <th>User</th>
                        <th>Status</th>
                        <th>Git SHA</th>
                        <th>Description</th>
                        <th>Project</th>
                      </tr>
                    </thead>
                    <tbody>
                      {buildLog && buildLog.length > 0 ? (
                        buildLog.map((line, index) => (
                          <tr key={index} className={line.status === 'failed' ? 'table-warning' : ''}>
                            <td className="cell-nowrap">{line.build_id}</td>
                            <td className="cell-nowrap">{new Date(line.timestamp).toLocaleString()}</td>
                            <td className="cell-nowrap">{line.user || "-"}</td>
                            <td className="cell-nowrap">{line.status}</td>
                            <td className="cell-nowrap">{line.git_sha}</td>
                            <td className="cell-nowrap">{line.description || "-"}</td>
                            <td className="cell-nowrap">{line.project || "-"}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="7">No logs available</td>
                        </tr>
                      )}
                    </tbody>
                    </Table>
                    </Accordion.Body>
                  </Accordion.Item>
                </Accordion>
              </Row>
            </Row>
          )}
        </Container>
      </section>
      <footer className="footer py-6 bg-dark text-white">
        <Container>
          <Row>
            <Col md={4}>
              <Navbar.Brand as={HashLink} to="#home" className="me-lg-3 mb-3 d-flex align-items-center">
                <Image src={ReactHero} />
                <span className="ms-2 brand-text">ME SCT</span>
              </Navbar.Brand>
              <p>This tool is a gateway to ME'S build system, allowing users to customize their software</p>
            </Col>
            <Col xs={6} md={2} className="mb-5 mb-lg-0">
              <span className="h5">Munich Electrification</span>
              <ul className="links-vertical mt-2">
                <li><Card.Link target="_blank" href="https://www.munichelectrification.com/">Blog</Card.Link></li>
                <li><Card.Link target="_blank" href="https://www.munichelectrification.com/">Products</Card.Link></li>
                <li><Card.Link target="_blank" href="https://www.munichelectrification.com/">About Us</Card.Link></li>
                <li><Card.Link target="_blank" href="https://www.munichelectrification.com/">Contact Us</Card.Link></li>
              </ul>
            </Col>
            <Col xs={6} md={2} className="mb-5 mb-lg-0">
              <span className="h5">Other</span>
              <ul className="links-vertical mt-2">
              </ul>
            </Col>
          </Row>
          <hr className="bg-gray my-5" />
          <Row>
            <Col className="mb-md-2">
              <div className="d-flex text-center justify-content-center align-items-center" role="contentinfo">
                <p className="font-weight-normal font-small mb-0">Copyright © Munich Electrification 2021-<span className="current-year">2022</span>. All rights reserved.</p>
              </div>
            </Col>
          </Row>
        </Container>
      </footer>
    </>
  );
};

export default SCTConfig;
